import apiClient from '../api/ApiClient';
import { ApiEndpoints } from '../../config/constants';
import { HttpStatusCode } from 'axios';

export interface AnalyticsSummary {
    totalVisits: number;
    uniqueVisitors: number;
    totalPageViews: number;
    totalProductViews: number;
    anonymousUsers: number;
    authenticatedUsers: number;
}

export interface AnalyticsSummaryWithTrends extends AnalyticsSummary {
    trends?: {
        totalVisits: number;
        uniqueVisitors: number;
        totalPageViews: number;
        totalProductViews: number;
        anonymousUsers: number;
        authenticatedUsers: number;
    };
}

export interface LinkedUser {
    userId: string;
    email: string;
    linkedAt: string;
}

export interface UserActivityWithDetails {
    id: string;
    anonymousId?: string;
    userId?: string;
    userEmail?: string;
    userName?: string;
    linkedUsers?: LinkedUser[];
    activityType: string;
    screenName?: string;
    screenPath?: string;
    activityData?: Record<string, any>;
    deviceType?: string;
    browser?: string;
    created: string;
}

export interface ActivityTimelineData {
    timePeriod: string;  // Can be hour, day, or month
    activityCount: number;
    pageViews: number;
    productViews: number;
    cartAdds: number;
    checkouts: number;
    signIns: number;
    signOuts: number;
    signUps: number;
    navigation: number;
    categoryFiltering: number;
}

class AnalyticsService {
    /**
     * Get analytics summary
     */
    async getAnalyticsSummary(startDate?: string, endDate?: string): Promise<{
        success: boolean;
        data?: AnalyticsSummary;
        message?: string;
    }> {
        try {
            const response = await apiClient.get(ApiEndpoints.Analytics.GetSummary(startDate, endDate));
            
            if (response.status === HttpStatusCode.Ok && response.data.data) {
                return {
                    success: true,
                    data: response.data.data
                };
            }

            return {
                success: false,
                message: response.data.errorMessage || 'Failed to fetch analytics summary'
            };
        } catch (error: any) {
            console.error('Error fetching analytics summary:', error);
            return {
                success: false,
                message: error.message || 'Failed to fetch analytics summary'
            };
        }
    }

    /**
     * Get recent user activities
     */
    async getRecentActivities(limit: number, offset: number = 0): Promise<{
        success: boolean;
        data?: UserActivityWithDetails[];
        message?: string;
    }> {
        try {
            const response = await apiClient.get(ApiEndpoints.Analytics.GetActivities(limit, offset));
            
            if (response.status === HttpStatusCode.Ok && response.data.data) {
                return {
                    success: true,
                    data: response.data.data
                };
            }

            return {
                success: false,
                message: response.data.errorMessage || 'Failed to fetch activities'
            };
        } catch (error: any) {
            console.error('Error fetching activities:', error);
            return {
                success: false,
                message: error.message || 'Failed to fetch activities'
            };
        }
    }

    /**
     * Get activity timeline for charts
     */
    async getActivityTimeline(
        startDate?: string,
        endDate?: string,
        activityType?: string,
        anonymousId?: string
    ): Promise<{
        success: boolean;
        data?: ActivityTimelineData[];
        message?: string;
    }> {
        try {
            // Get user's browser timezone
            const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
            const response = await apiClient.get(
                ApiEndpoints.Analytics.GetTimeline(startDate, endDate, activityType, anonymousId, userTimezone)
            );
            
            if (response.status === HttpStatusCode.Ok && response.data.data) {
                return {
                    success: true,
                    data: response.data.data
                };
            }

            return {
                success: false,
                message: response.data.errorMessage || 'Failed to fetch timeline'
            };
        } catch (error: any) {
            console.error('Error fetching timeline:', error);
            return {
                success: false,
                message: error.message || 'Failed to fetch timeline'
            };
        }
    }

    /**
     * Get top products by views
     */
    async getTopProducts(limit: number = 10, startDate?: string, endDate?: string): Promise<{
        success: boolean;
        data?: Array<{
            productId: string;
            productName: string;
            viewCount: number;
        }>;
        message?: string;
    }> {
        try {
            const response = await apiClient.get(ApiEndpoints.Analytics.GetTopProducts(limit, startDate, endDate));
            
            if (response.status === HttpStatusCode.Ok && response.data.data) {
                return {
                    success: true,
                    data: response.data.data
                };
            }

            return {
                success: false,
                message: response.data.errorMessage || 'Failed to fetch top products'
            };
        } catch (error: any) {
            console.error('Error fetching top products:', error);
            return {
                success: false,
                message: error.message || 'Failed to fetch top products'
            };
        }
    }

    /**
     * Get all products with their view statistics
     */
    async getProductsWithViews(startDate?: string, endDate?: string): Promise<{
        success: boolean;
        data?: Array<{
            productId: string;
            productName: string;
            productCode: string;
            category: string;
            price: number;
            discount: string | null;
            discountType?: 'percentage' | 'value';
            stockOnHand: number;
            inStock: boolean;
            isOnSale: boolean;
            totalViews: number;
            uniqueViews: number;
            uniqueAnonymousViews: number;
            uniqueUserViews: number;
            lastViewed: string | null;
        }>;
        message?: string;
    }> {
        try {
            const response = await apiClient.get(ApiEndpoints.Analytics.GetProductsWithViews(startDate, endDate));
            
            if (response.status === HttpStatusCode.Ok && response.data.data) {
                return {
                    success: true,
                    data: response.data.data
                };
            }

            return {
                success: false,
                message: response.data.errorMessage || 'Failed to fetch products with views'
            };
        } catch (error: any) {
            console.error('Error fetching products with views:', error);
            return {
                success: false,
                message: error.message || 'Failed to fetch products with views'
            };
        }
    }

    /**
     * Get low stock products
     */
    async getLowStockProducts(): Promise<{
        success: boolean;
        data?: Array<{
            productId: string;
            productName: string;
            productCode: string;
            category: string;
            price: number;
            discount: string | null;
            stockOnHand: number;
            stockLowThreshold: number;
            inStock: boolean;
            totalViews: number;
        }>;
        message?: string;
    }> {
        try {
            const response = await apiClient.get(ApiEndpoints.Analytics.GetLowStockProducts);
            
            if (response.status === HttpStatusCode.Ok && response.data.data) {
                return {
                    success: true,
                    data: response.data.data
                };
            }

            return {
                success: false,
                message: response.data.errorMessage || 'Failed to fetch low stock products'
            };
        } catch (error: any) {
            console.error('Error fetching low stock products:', error);
            return {
                success: false,
                message: error.message || 'Failed to fetch low stock products'
            };
        }
    }

    /**
     * Get top products by cart additions
     */
    async getTopCartProducts(limit: number = 10): Promise<{
        success: boolean;
        data?: Array<{
            productId: string;
            productName: string;
            productCode: string;
            category: string;
            price: number;
            discount: string | null;
            cartAddCount: number;
            viewCount: number;
            conversionRate: number;
        }>;
        message?: string;
    }> {
        try {
            const response = await apiClient.get(ApiEndpoints.Analytics.GetTopCartProducts(limit));
            
            if (response.status === HttpStatusCode.Ok && response.data.data) {
                return {
                    success: true,
                    data: response.data.data
                };
            }

            return {
                success: false,
                message: response.data.errorMessage || 'Failed to fetch top cart products'
            };
        } catch (error: any) {
            console.error('Error fetching top cart products:', error);
            return {
                success: false,
                message: error.message || 'Failed to fetch top cart products'
            };
        }
    }

    /**
     * Get sales vs non-sales views comparison
     */
    async getSalesVsNonsales(startDate?: string, endDate?: string): Promise<{
        success: boolean;
        data?: Array<{
            priceType: string;
            totalViews: number;
            uniqueViews: number;
            productCount: number;
            avgViewsPerProduct: number;
        }>;
        message?: string;
    }> {
        try {
            const response = await apiClient.get(ApiEndpoints.Analytics.GetSalesVsNonsales(startDate, endDate));
            
            if (response.status === HttpStatusCode.Ok && response.data.data) {
                return {
                    success: true,
                    data: response.data.data
                };
            }

            return {
                success: false,
                message: response.data.errorMessage || 'Failed to fetch sales vs non-sales data'
            };
        } catch (error: any) {
            console.error('Error fetching sales vs non-sales:', error);
            return {
                success: false,
                message: error.message || 'Failed to fetch sales vs non-sales data'
            };
        }
    }

    /**
     * Get category performance
     */
    async getCategoryPerformance(): Promise<{
        success: boolean;
        data?: Array<{
            category: string;
            productCount: number;
            totalViews: number;
            uniqueViews: number;
            avgViewsPerProduct: number;
            productsOnSale: number;
            totalStock: number;
            lowStockCount: number;
        }>;
        message?: string;
    }> {
        try {
            const response = await apiClient.get(ApiEndpoints.Analytics.GetCategoryPerformance);
            
            if (response.status === HttpStatusCode.Ok && response.data.data) {
                return {
                    success: true,
                    data: response.data.data
                };
            }

            return {
                success: false,
                message: response.data.errorMessage || 'Failed to fetch category performance'
            };
        } catch (error: any) {
            console.error('Error fetching category performance:', error);
            return {
                success: false,
                message: error.message || 'Failed to fetch category performance'
            };
        }
    }

    /**
     * Get lowest stock products (top N, regardless of threshold)
     */
    async getLowestStockProducts(limit: number = 10): Promise<{
        success: boolean;
        data?: Array<{
            productId: string;
            productName: string;
            productCode: string;
            category: string;
            price: number;
            discount: string | null;
            stockOnHand: number;
            stockLowThreshold: number;
            inStock: boolean;
            totalViews: number;
        }>;
        message?: string;
    }> {
        try {
            const response = await apiClient.get(ApiEndpoints.Analytics.GetLowestStockProducts(limit));
            
            if (response.status === HttpStatusCode.Ok && response.data.data) {
                return {
                    success: true,
                    data: response.data.data
                };
            }

            return {
                success: false,
                message: response.data.errorMessage || 'Failed to fetch lowest stock products'
            };
        } catch (error: any) {
            console.error('Error fetching lowest stock products:', error);
            return {
                success: false,
                message: error.message || 'Failed to fetch lowest stock products'
            };
        }
    }

    /**
     * Get product views over time
     */
    async getProductViewsOverTime(
        startDate?: string,
        endDate?: string,
        granularity: string = 'day'
    ): Promise<{
        success: boolean;
        data?: Array<{
            timePeriod: string;
            totalViews: number;
            uniqueViews: number;
            productCount: number;
        }>;
        message?: string;
    }> {
        try {
            const response = await apiClient.get(
                ApiEndpoints.Analytics.GetProductViewsOverTime(startDate, endDate, granularity)
            );
            
            if (response.status === HttpStatusCode.Ok && response.data.data) {
                return {
                    success: true,
                    data: response.data.data
                };
            }

            return {
                success: false,
                message: response.data.errorMessage || 'Failed to fetch product views over time'
            };
        } catch (error: any) {
            console.error('Error fetching product views over time:', error);
            return {
                success: false,
                message: error.message || 'Failed to fetch product views over time'
            };
        }
    }

    /**
     * Get product summary statistics
     */
    async getProductSummaryStats(startDate?: string, endDate?: string): Promise<{
        success: boolean;
        data?: {
            totalProducts: number;
            activeProducts: number;
            totalViews: number;
            avgViewsPerProduct: number;
            productsOnSale: number;
            productsWithZeroViews: number;
            totalStock: number;
        };
        message?: string;
    }> {
        try {
            const response = await apiClient.get(
                ApiEndpoints.Analytics.GetProductSummaryStats(startDate, endDate)
            );
            
            if (response.status === HttpStatusCode.Ok && response.data.data) {
                return {
                    success: true,
                    data: response.data.data
                };
            }

            return {
                success: false,
                message: response.data.errorMessage || 'Failed to fetch product summary stats'
            };
        } catch (error: any) {
            console.error('Error fetching product summary stats:', error);
            return {
                success: false,
                message: error.message || 'Failed to fetch product summary stats'
            };
        }
    }

    /**
     * Get stock level distribution
     */
    async getStockLevelDistribution(): Promise<{
        success: boolean;
        data?: Array<{
            stockLevel: string;
            productCount: number;
            totalStock: number;
        }>;
        message?: string;
    }> {
        try {
            const response = await apiClient.get(ApiEndpoints.Analytics.GetStockLevelDistribution);
            
            if (response.status === HttpStatusCode.Ok && response.data.data) {
                return {
                    success: true,
                    data: response.data.data
                };
            }

            return {
                success: false,
                message: response.data.errorMessage || 'Failed to fetch stock level distribution'
            };
        } catch (error: any) {
            console.error('Error fetching stock level distribution:', error);
            return {
                success: false,
                message: error.message || 'Failed to fetch stock level distribution'
            };
        }
    }

    /**
     * Get price range distribution
     */
    async getPriceRangeDistribution(): Promise<{
        success: boolean;
        data?: Array<{
            priceRange: string;
            productCount: number;
            totalValue: number;
        }>;
        message?: string;
    }> {
        try {
            const response = await apiClient.get(ApiEndpoints.Analytics.GetPriceRangeDistribution);
            
            if (response.status === HttpStatusCode.Ok && response.data.data) {
                return {
                    success: true,
                    data: response.data.data
                };
            }

            return {
                success: false,
                message: response.data.errorMessage || 'Failed to fetch price range distribution'
            };
        } catch (error: any) {
            console.error('Error fetching price range distribution:', error);
            return {
                success: false,
                message: error.message || 'Failed to fetch price range distribution'
            };
        }
    }

    /**
     * Get product activity summary
     */
    async getProductActivitySummary(startDate?: string, endDate?: string): Promise<{
        success: boolean;
        data?: {
            mostViewedProductId: string | null;
            mostViewedProductName: string | null;
            mostViewedCount: number;
            leastViewedProductId: string | null;
            leastViewedProductName: string | null;
            leastViewedCount: number;
            avgViews: number;
            zeroViewProducts: number;
        };
        message?: string;
    }> {
        try {
            const response = await apiClient.get(
                ApiEndpoints.Analytics.GetProductActivitySummary(startDate, endDate)
            );
            
            if (response.status === HttpStatusCode.Ok && response.data.data) {
                return {
                    success: true,
                    data: response.data.data
                };
            }

            return {
                success: false,
                message: response.data.errorMessage || 'Failed to fetch product activity summary'
            };
        } catch (error: any) {
            console.error('Error fetching product activity summary:', error);
            return {
                success: false,
                message: error.message || 'Failed to fetch product activity summary'
            };
        }
    }

    /**
     * Get views vs stock correlation
     */
    async getViewsStockCorrelation(startDate?: string, endDate?: string): Promise<{
        success: boolean;
        data?: Array<{
            productId: string;
            productName: string;
            stockOnHand: number;
            viewCount: number;
        }>;
        message?: string;
    }> {
        try {
            const response = await apiClient.get(
                ApiEndpoints.Analytics.GetViewsStockCorrelation(startDate, endDate)
            );
            
            if (response.status === HttpStatusCode.Ok && response.data.data) {
                return {
                    success: true,
                    data: response.data.data
                };
            }

            return {
                success: false,
                message: response.data.errorMessage || 'Failed to fetch views stock correlation'
            };
        } catch (error: any) {
            console.error('Error fetching views stock correlation:', error);
            return {
                success: false,
                message: error.message || 'Failed to fetch views stock correlation'
            };
        }
    }

    /**
     * Get discount impact analysis
     */
    async getDiscountImpact(startDate?: string, endDate?: string): Promise<{
        success: boolean;
        data?: Array<{
            hasDiscount: boolean;
            productCount: number;
            totalViews: number;
            avgViewsPerProduct: number;
            uniqueViews: number;
        }>;
        message?: string;
    }> {
        try {
            const response = await apiClient.get(
                ApiEndpoints.Analytics.GetDiscountImpact(startDate, endDate)
            );
            
            if (response.status === HttpStatusCode.Ok && response.data.data) {
                return {
                    success: true,
                    data: response.data.data
                };
            }

            return {
                success: false,
                message: response.data.errorMessage || 'Failed to fetch discount impact'
            };
        } catch (error: any) {
            console.error('Error fetching discount impact:', error);
            return {
                success: false,
                message: error.message || 'Failed to fetch discount impact'
            };
        }
    }

    /**
     * Get product freshness score
     */
    async getProductFreshness(daysThreshold?: number): Promise<{
        success: boolean;
        data?: {
            newProductsCount: number;
            newProductsViews: number;
            oldProductsCount: number;
            oldProductsViews: number;
            newProductsZeroViews: number;
        };
        message?: string;
    }> {
        try {
            const response = await apiClient.get(
                ApiEndpoints.Analytics.GetProductFreshness(daysThreshold)
            );
            
            if (response.status === HttpStatusCode.Ok && response.data.data) {
                return {
                    success: true,
                    data: response.data.data
                };
            }

            return {
                success: false,
                message: response.data.errorMessage || 'Failed to fetch product freshness'
            };
        } catch (error: any) {
            console.error('Error fetching product freshness:', error);
            return {
                success: false,
                message: error.message || 'Failed to fetch product freshness'
            };
        }
    }

    /**
     * Get category stock health
     */
    async getCategoryStockHealth(): Promise<{
        success: boolean;
        data?: Array<{
            category: string;
            totalStock: number;
            lowStockCount: number;
            mediumStockCount: number;
            highStockCount: number;
            stockHealthPercentage: number;
        }>;
        message?: string;
    }> {
        try {
            const response = await apiClient.get(ApiEndpoints.Analytics.GetCategoryStockHealth);
            
            if (response.status === HttpStatusCode.Ok && response.data.data) {
                return {
                    success: true,
                    data: response.data.data
                };
            }

            return {
                success: false,
                message: response.data.errorMessage || 'Failed to fetch category stock health'
            };
        } catch (error: any) {
            console.error('Error fetching category stock health:', error);
            return {
                success: false,
                message: error.message || 'Failed to fetch category stock health'
            };
        }
    }
}

// Export singleton instance
export const analyticsService = new AnalyticsService();
export default analyticsService;


